import { useEffect, useState } from 'react';
import { Button, Divider, Stack, Typography } from '@mui/material';
import PropertyCard from './property-card';
import { LabeledInput } from '../shared/labeled-input';
import { SmallCheckBox as SmallCheckBoxBase } from '../shared/small-check-box';
import { SearchAsYouType as SearchAsYouTypeBase } from '../shared/search-as-you-type';
import Modal from '../shared/modal';
import TableWrapperBase from '../shared/table';
import { dataTypes } from '../shared/table-helpers';
import { sortTypes } from '../shared/sorting';
import { useNotificationContext } from '../shared/notification';
import { ItemAPI } from '../../api/item';
import { getCustomerLookupOptions } from '../../api/customer';
import {
  shippableLocationIds,
  warehouseFriendlyAbbreviations,
} from '../../constants/warehouse-codes';

/* eslint-disable @typescript-eslint/no-explicit-any */
const TableWrapper = TableWrapperBase as any;
const SearchAsYouType = SearchAsYouTypeBase as any;
const SmallCheckBox = SmallCheckBoxBase as any;

/// The warehouses a missed opportunity can be recorded against. Production
/// locations are intentionally excluded -- a missed opportunity is about
/// sellable stock, not a production line.
const warehouseOptions = Object.values(shippableLocationIds).map((id) => ({
  id,
  label: warehouseFriendlyAbbreviations[id],
}));

/// A missed opportunity as it comes back from the API. `warehouses` is stored
/// as a TEXT column holding a JSON array, so it arrives as a string, and
/// `createdBy` is resolved to a user's display name (null if unmatched).
interface MissedOpportunityRow {
  id: number;
  customerId: number;
  customer_name?: string;
  createdBy?: string;
  createdDate: string;
  quantity: number;
  warehouses: string;
  existingBusiness?: boolean;
}

const parseWarehouses = (warehouses: string): string => {
  try {
    const ids = JSON.parse(warehouses ?? '[]') as number[];
    return ids.map((id) => warehouseFriendlyAbbreviations[id] ?? id).join(', ');
  } catch {
    return '';
  }
};

/// Self-contained missed opportunity capture for the item page: records a
/// quantity/customer/warehouse combination we couldn't fill, and lists the
/// ones already recorded for this item.
export default function MissedOpportunity({ itemId }: { itemId: string }) {
  const { useExceptionHandler, showMessage } = useNotificationContext();

  const [opportunities, setOpportunities] = useState<MissedOpportunityRow[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [showOpportunities, setShowOpportunities] = useState(false);

  const [quantity, setQuantity] = useState('');
  const [customerId, setCustomerId] = useState<number | string>('');
  const [warehouses, setWarehouses] = useState<number[]>([]);
  const [existingBusiness, setExistingBusiness] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadOpportunities = useExceptionHandler(
    async (id: string) => {
      setLoading(true);
      const { data } = await ItemAPI.getMissedOpportunities({ itemId: id });
      setOpportunities(data ?? []);
    },
    () => setLoading(false)
  );

  useEffect(() => {
    if (!itemId) return;
    resetForm();
    setShowOpportunities(false);
    loadOpportunities(itemId);
  }, [itemId]);

  const resetForm = () => {
    setQuantity('');
    setCustomerId('');
    setWarehouses([]);
    setExistingBusiness(false);
  };

  const toggleWarehouse = (id: number) =>
    setWarehouses((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]
    );

  const isValid =
    !!quantity && +quantity > 0 && !!customerId && warehouses.length > 0;

  const submit = useExceptionHandler(
    async () => {
      setSubmitting(true);
      await ItemAPI.createMissedOpportunity({
        itemId,
        customerId: +customerId,
        quantity: +quantity,
        warehouses,
        existingBusiness,
      });
      showMessage({
        message: 'Missed opportunity recorded.',
        severity: 'success',
      });
      resetForm();
      await loadOpportunities(itemId);
    },
    () => setSubmitting(false)
  );

  const columns = [
    {
      id: 'createdDate',
      label: 'Date',
      dataType: dataTypes.date,
      isSortable: true,
    },
    { id: 'customerId', label: 'Customer #', isSortable: true },
    { id: 'customerName', label: 'Customer Name', isSortable: true },
    {
      id: 'quantity',
      label: 'Quantity',
      dataType: dataTypes.quantity,
      isSortable: true,
    },
    { id: 'warehouses', label: 'Warehouses' },
    { id: 'existingBusiness', label: 'Existing Business', isSortable: true },
    { id: 'createdBy', label: 'Created By', isSortable: true },
  ];

  const rows = opportunities.map((row) => ({
    createdDate: row.createdDate,
    customerId: row.customerId,
    customerName: row.customer_name,
    quantity: row.quantity,
    warehouses: parseWarehouses(row.warehouses),
    existingBusiness: row.existingBusiness ? 'Y' : 'N',
    createdBy: row.createdBy,
  }));

  return (
    <PropertyCard title='Missed Opportunity'>
      <Stack direction={'column'} gap={1.5} sx={{ padding: '10px' }}>
        <Stack direction={'column'} gap={1.5} useFlexGap>
          <LabeledInput
            label='Quantity'
            type='number'
            variant='outlined'
            value={quantity}
            updateOnChangeLive
            onChange={(e) => {
              const event = e as React.ChangeEvent<HTMLInputElement>;
              setQuantity(event.target.value);
            }}
          />
          <SearchAsYouType
            label='Customer #'
            variant='outlined'
            value={customerId}
            placeholder='Type in account info...'
            onChange={(e: { target?: { value?: number | string } }) =>
              setCustomerId(e?.target?.value ?? '')
            }
            getSearchOptions={(searchInput: string) =>
              getCustomerLookupOptions({ searchString: searchInput })
            }
          />
        </Stack>
        <Stack direction={'row'} gap={1} useFlexGap flexWrap={'wrap'}>
          {warehouseOptions.map((warehouse) => (
            <SmallCheckBox
              key={warehouse.id}
              label={warehouse.label}
              checked={warehouses.includes(warehouse.id)}
              onChange={() => toggleWarehouse(warehouse.id)}
            />
          ))}
        </Stack>
        <SmallCheckBox
          label='Existing Business'
          checked={existingBusiness}
          onChange={() => setExistingBusiness((prev) => !prev)}
        />
        <Button
          variant='contained'
          disabled={!isValid || submitting}
          onClick={() => submit()}
        >
          Submit
        </Button>
        <Divider />
        <Button
          variant='text'
          disabled={loading}
          onClick={() => setShowOpportunities(true)}
        >
          {loading
            ? 'Loading Missed Opportunities...'
            : `View ${opportunities.length} Missed ${
                opportunities.length === 1 ? 'Opportunity' : 'Opportunities'
              }`}
        </Button>
      </Stack>
      {showOpportunities && (
        <Modal
          open={showOpportunities}
          setOpen={setShowOpportunities}
          allowBackdropClose
          title={
            <Typography variant='h6'>
              Missed Opportunities for Item {itemId}
            </Typography>
          }
        >
          <TableWrapper
            showSearch
            transformRows
            orderBy={'createdDate'}
            orderDirection={sortTypes.desc}
            columns={columns}
            rows={rows}
            showLoader={loading}
          />
        </Modal>
      )}
    </PropertyCard>
  );
}
